'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';

export interface TooltipProps {
  children: React.ReactNode;
  title: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  theme?: 'dark' | 'light';
}

const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const TooltipTrigger = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TooltipContent = styled.div<{ $visible: boolean; $position: 'top' | 'bottom' | 'left' | 'right'; $theme: 'dark' | 'light' }>`
  position: absolute;
  background: ${({ $theme }) => ($theme === 'light' ? '#ffffff' : '#1f2937')};
  color: ${({ $theme }) => ($theme === 'light' ? '#000000' : '#ffffff')};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  box-shadow: ${theme.shadows.md};
  font-family: 'Open Sans', sans-serif;
  border: ${({ $theme }) => ($theme === 'light' ? '1px solid #e5e7eb' : 'none')};

  ${({ $position }) => {
    switch ($position) {
      case 'top':
        return `
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'bottom':
        return `
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'left':
        return `
          right: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
        `;
      case 'right':
        return `
          left: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
        `;
      default:
        return '';
    }
  }}

  /* Arrow */
  &::after {
    content: '';
    position: absolute;
    border: 6px solid transparent;

    ${({ $position, $theme }) => {
      const arrowColor = $theme === 'light' ? '#ffffff' : '#1f2937';
      switch ($position) {
        case 'top':
          return `
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-top-color: ${arrowColor};
          `;
        case 'bottom':
          return `
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-bottom-color: ${arrowColor};
          `;
        case 'left':
          return `
            left: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-left-color: ${arrowColor};
          `;
        case 'right':
          return `
            right: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-right-color: ${arrowColor};
          `;
        default:
          return '';
      }
    }}
  }

  /* Arrow border for light theme */
  ${({ $theme }) => $theme === 'light' && `
    &::before {
      content: '';
      position: absolute;
      border: 6px solid transparent;

      ${({ $position }: { $position: string }) => {
        switch ($position) {
          case 'top':
            return `
              top: 100%;
              left: 50%;
              transform: translateX(-50%);
              border-top-color: #e5e7eb;
              margin-top: 1px;
            `;
          case 'bottom':
            return `
              bottom: 100%;
              left: 50%;
              transform: translateX(-50%);
              border-bottom-color: #e5e7eb;
              margin-bottom: 1px;
            `;
          case 'left':
            return `
              left: 100%;
              top: 50%;
              transform: translateY(-50%);
              border-left-color: #e5e7eb;
              margin-left: 1px;
            `;
          case 'right':
            return `
              right: 100%;
              top: 50%;
              transform: translateY(-50%);
              border-right-color: #e5e7eb;
              margin-right: 1px;
            `;
          default:
            return '';
        }
      }}
    }
  `}
`;

/**
 * Tooltip Component
 * 
 * Displays a tooltip with title text when hovering over children element.
 * Supports both dark (default) and light themes.
 * 
 * @component
 * @example
 * ```tsx
 * // Dark theme (default)
 * <Tooltip title="This is helpful information">
 *   <InfoIcon />
 * </Tooltip>
 * 
 * // Light theme
 * <Tooltip title="This is helpful information" theme="light">
 *   <InfoIcon />
 * </Tooltip>
 * ```
 */
export const Tooltip: React.FC<TooltipProps> = ({ 
  children, 
  title, 
  position = 'top',
  theme = 'dark'
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <TooltipContainer
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent $visible={visible} $position={position} $theme={theme}>
        {title}
      </TooltipContent>
    </TooltipContainer>
  );
};

export default Tooltip;

